/**
 * Nara site interactions: mobile nav, Ask Nara (orb + bubbles), living plan, quiz.
 */

const NARA_ASK_STORAGE = 'nara-web-ask-count';
const THINKING_MS = 780;
const ASK_VOICE_PHASES = Object.freeze(['listening', 'thinking', 'processing', 'speaking']);
const ASK_PHASE_FADE_MS = 720;
const ASK_IDLE_HOLD_MS = 320;
const ASK_ORB_BOUNCE_MS = 320;

function naraSleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

class NaraLandingNav extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.lastY = window.scrollY;
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.sync();
      });
    };
    this.onReturn = () => {
      this.lastY = window.scrollY;
      this.removeAttribute('data-hidden');
      this.sync();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.abort.signal });
    window.addEventListener('pageshow', this.onReturn, { signal: this.abort.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.onReturn();
    }, { signal: this.abort.signal });
    this.removeAttribute('data-hidden');
    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  sync() {
    this.syncSolid();
    this.syncBanner();
    this.syncHidden();
  }

  syncSolid() {
    const hero = document.querySelector('.nara-landing-hero');
    if (!hero) return;
    const passed = window.scrollY > 12;
    if (passed) this.setAttribute('data-solid', '');
    else this.removeAttribute('data-solid');
  }

  syncBanner() {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.removeAttribute('data-past-hero');
      return;
    }
    const pastTop = Math.max(0, window.scrollY) > 12;
    this.toggleAttribute('data-past-hero', pastTop);
  }

  syncHidden() {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.removeAttribute('data-hidden');
      return;
    }

    const y = Math.max(0, window.scrollY);
    const delta = y - this.lastY;
    this.lastY = y;
    const banner = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--nara-launch-banner-h')
    ) || 0;
    const stay = 64 + banner;

    if (y < stay || this.contains(document.activeElement) || delta < 0) {
      this.removeAttribute('data-hidden');
      return;
    }

    if (delta > 8 && y > stay) this.setAttribute('data-hidden', '');
  }
}

class NaraAskPreview extends HTMLElement {
  connectedCallback() {
    this.orb = this.querySelector('[data-nara-ask-orb]');
    this.voiceFrame = this.querySelector('[data-nara-ask-voice]');
    this.voiceReady = false;
    this.pendingVoiceState = null;
    this.thread = this.querySelector('[data-nara-ask-thread]');
    this.form = this.querySelector('[data-nara-ask-form]');
    this.input = this.querySelector('[data-nara-ask-input]');
    this.send = this.querySelector('[data-nara-ask-send]');
    this.hints = this.querySelector('[data-nara-ask-hints]');
    this.reply = this.querySelector('[data-nara-ask-reply]');
    this.status = this.querySelector('[data-nara-ask-status]');
    this.limit = this.querySelector('[data-nara-ask-limit]');
    this.history = [];
    this.busy = false;
    this.audio = null;
    this.phaseIndex = 0;
    this.lastPhaseTapAt = 0;
    this.orbMorphTimer = 0;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.voiceFrame?.addEventListener(
      'load',
      () => {
        this.voiceReady = true;
        const phase = this.orb?.getAttribute('data-orb-phase');
        const layer = this.orb?.getAttribute('data-orb-layer');
        if (layer === 'voice' && phase && phase !== 'loading') {
          this.postVoiceState('idle');
          this.armVoiceMorph(phase);
          return;
        }
        if (this.pendingVoiceState) this.postVoiceState(this.pendingVoiceState);
      },
      { signal }
    );
    this.bootVoiceFrame();
    this.bindOrbPhaseTap();

    this.cap = Math.max(1, Number(this.getAttribute('data-ask-cap') || 4) || 4);
    this.fallback = this.getAttribute('data-fallback') || 'I can walk through this with you in the Nara app.';

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    }, { signal });

    this.input?.addEventListener('input', () => {
      this.syncSend();
      this.syncListenMode();
    }, { signal });
    this.input?.addEventListener('focus', () => this.syncListenMode(), { signal });
    this.input?.addEventListener('blur', () => {
      if (!this.busy && !this.input?.value.trim()) {
        this.resetVoiceDemo();
        this.setOrb('loading');
      }
    }, { signal });

    this.querySelectorAll('[data-nara-ask-hint]').forEach((button) => {
      button.addEventListener('click', () => this.fillHint(button), { signal });
    });

    this.syncSend();
    if (this.used() >= this.cap) this.lock();
  }

  disconnectedCallback() {
    this.clearOrbTimers();
    this.stopAudio();
    this.abort?.abort();
  }

  clearOrbTimers() {
    if (this.orbMorphTimer) {
      window.clearTimeout(this.orbMorphTimer);
      this.orbMorphTimer = 0;
    }
  }

  armVoiceMorph(state) {
    this.clearOrbTimers();
    this.orbMorphTimer = window.setTimeout(() => {
      this.orbMorphTimer = 0;
      this.postVoiceState(state);
    }, this.motionOk() ? ASK_IDLE_HOLD_MS : 0);
  }

  used() {
    const raw = Number(sessionStorage.getItem(NARA_ASK_STORAGE) || 0);
    return Number.isFinite(raw) ? raw : 0;
  }

  markUsed() {
    sessionStorage.setItem(NARA_ASK_STORAGE, String(this.used() + 1));
  }

  fillHint(button) {
    if (this.busy || this.used() >= this.cap) return;
    const prompt = button.getAttribute('data-prompt')?.trim();
    if (!prompt || !this.input) return;
    this.input.value = prompt;
    this.input.focus();
    this.syncSend();
    this.syncListenMode();
  }

  syncSend() {
    const hasText = Boolean(this.input?.value.trim());
    const canSend = hasText && !this.busy && this.used() < this.cap;
    this.send?.toggleAttribute('data-ready', hasText && this.used() < this.cap);
    if (this.send) this.send.disabled = !canSend;
  }

  setBusy(busy) {
    this.busy = busy;
    if (this.input) this.input.readOnly = busy;
    this.querySelectorAll('[data-nara-ask-hint]').forEach((button) => {
      button.disabled = busy || this.used() >= this.cap;
    });
    this.syncSend();
  }

  showStatus(on) {
    if (!this.status) return;
    if (on) this.status.removeAttribute('hidden');
    else this.status.setAttribute('hidden', '');
  }

  lock() {
    this.form?.setAttribute('data-disabled', '');
    if (this.input) {
      this.input.readOnly = true;
      this.input.disabled = true;
    }
    if (this.send) this.send.disabled = true;
    this.send?.removeAttribute('data-ready');
    this.querySelectorAll('[data-nara-ask-hint]').forEach((button) => {
      button.disabled = true;
    });
    this.limit?.removeAttribute('hidden');
  }

  motionOk() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  async bootVoiceFrame() {
    if (!this.motionOk()) return;
    const frame = this.voiceFrame;
    const src = frame?.getAttribute('src');
    if (!frame || !src) return;
    try {
      const html = await fetch(src).then((response) => response.text());
      if (!html || !html.includes('nara-orbe-voice26')) return;
      frame.removeAttribute('src');
      frame.srcdoc = html;
    } catch {
      // Keep the asset URL iframe if fetch is blocked.
    }
  }

  setOrb(mode) {
    const loading = this.querySelector('.nara-landing-ask__orb--loading');
    const frame = this.voiceFrame;
    const layer = this.orb?.getAttribute('data-orb-layer') || 'loading';
    if (!this.motionOk()) {
      this.clearOrbTimers();
      this.orb?.setAttribute('data-orb-layer', 'loading');
      this.orb?.setAttribute('data-orb-phase', 'loading');
      return;
    }
    const voice = {
      anneaux: 'listening',
      listening: 'listening',
      thinking: 'thinking',
      processing: 'processing',
      speaking: 'speaking',
    };
    const state = voice[mode] || null;
    if (!state) {
      this.orb?.setAttribute('data-orb-phase', 'loading');
      this.clearOrbTimers();
      this.postVoiceState('idle');
      // After the first handoff, idle on the voice canvas is the gold sphere.
      if (layer === 'voice') return;
      this.orb?.setAttribute('data-orb-layer', 'loading');
      if (loading) loading.style.zIndex = '2';
      if (frame) frame.style.zIndex = '1';
      return;
    }
    this.orb?.setAttribute('data-orb-phase', state);
    if (layer !== 'voice') {
      this.postVoiceState('idle');
      this.orb?.setAttribute('data-orb-layer', 'voice');
      if (loading) loading.style.zIndex = '1';
      if (frame) frame.style.zIndex = '2';
      this.armVoiceMorph(state);
      return;
    }
    this.clearOrbTimers();
    this.postVoiceState(state);
  }

  resetVoiceDemo() {
    this.phaseIndex = 0;
  }

  bindOrbPhaseTap() {
    const stage = this.orb;
    if (!stage) return;
    const { signal } = this.abort;
    let startX = 0;
    let startY = 0;
    stage.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );
    stage.addEventListener(
      'pointerup',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > 12) return;
        this.cycleVoicePhase();
      },
      { signal }
    );
    stage.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.cycleVoicePhase();
      },
      { signal }
    );
  }

  cycleVoicePhase() {
    if (this.busy || this.used() >= this.cap) return;
    if (this.input?.value.trim()) return;
    if (!this.motionOk()) return;
    const now = performance.now();
    if (now - this.lastPhaseTapAt < ASK_PHASE_FADE_MS - 80) return;
    this.lastPhaseTapAt = now;
    const loop = [...ASK_VOICE_PHASES, 'loading'];
    const next = loop[this.phaseIndex % loop.length] || 'loading';
    this.phaseIndex = (this.phaseIndex + 1) % loop.length;
    this.setOrb(next);
    this.bounceOrb();
  }

  bounceOrb() {
    if (!this.motionOk() || !this.orb) return;
    this.orb.classList.remove('is-bounce');
    void this.orb.offsetWidth;
    this.orb.classList.add('is-bounce');
    window.setTimeout(() => this.orb?.classList.remove('is-bounce'), ASK_ORB_BOUNCE_MS + 40);
  }

  postVoiceState(state) {
    const frame = this.voiceFrame;
    if (!frame?.contentWindow || !this.voiceReady) {
      this.pendingVoiceState = state;
      return;
    }
    this.pendingVoiceState = null;
    frame.contentWindow.postMessage({ type: 'nara-orb-state', state }, '*');
  }

  syncListenMode() {
    if (this.busy) return;
    if (this.input?.value.trim()) {
      this.resetVoiceDemo();
      this.setOrb('listening');
      return;
    }
    if (document.activeElement === this.input) {
      this.setOrb('listening');
      return;
    }
    this.setOrb('loading');
  }

  addBubble(role, text = '') {
    const bubble = document.createElement('p');
    bubble.className = `nara-landing-ask__bubble nara-landing-ask__bubble--${role}`;
    bubble.textContent = text;
    this.thread?.append(bubble);
    bubble.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return bubble;
  }

  resetReply() {
    if (this.thread) this.thread.replaceChildren();
    this.reply?.removeAttribute('hidden');
  }

  async typewrite(el, text, durationMs) {
    const chars = Array.from(text);
    if (!chars.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      el.textContent = text;
      return;
    }
    const step = Math.max(16, durationMs / chars.length);
    el.textContent = '';
    for (let index = 0; index < chars.length; index += 1) {
      el.textContent += chars[index];
      await naraSleep(step);
    }
  }

  headers() {
    const headers = { 'Content-Type': 'application/json' };
    const key = this.getAttribute('data-ask-key')?.trim();
    if (key) {
      headers.apikey = key;
      headers.Authorization = `Bearer ${key}`;
    }
    return headers;
  }

  async fetchReply(message) {
    const endpoint = this.getAttribute('data-ask-endpoint')?.trim();
    if (!endpoint) return this.fallback;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        message,
        history: this.history.slice(-6),
      }),
    });
    if (!response.ok) throw new Error(`ask ${response.status}`);
    const data = await response.json();
    return (data.reply || data.text || '').trim() || this.fallback;
  }

  async fetchSpeech(text) {
    const endpoint = this.getAttribute('data-tts-endpoint')?.trim();
    if (!endpoint) return null;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ text }),
    });
    if (!response.ok) return null;
    const type = response.headers.get('content-type') || '';
    if (type.includes('application/json')) {
      const data = await response.json();
      if (data.audio_base64) {
        const bytes = Uint8Array.from(atob(data.audio_base64), (char) => char.charCodeAt(0));
        return new Blob([bytes], { type: data.content_type || 'audio/mpeg' });
      }
      return null;
    }
    return response.blob();
  }

  stopAudio() {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
  }

  async speak(blob) {
    if (!blob || blob.size < 32) return null;
    this.stopAudio();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    this.audio = audio;
    try {
      await audio.play();
    } catch {
      URL.revokeObjectURL(url);
      return null;
    }
    return new Promise((resolve) => {
      const finish = () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration || null);
      };
      audio.addEventListener('ended', finish, { once: true });
      audio.addEventListener('error', finish, { once: true });
    });
  }

  async submit() {
    const message = this.input?.value.trim();
    if (!message || this.busy) return;
    if (this.used() >= this.cap) {
      this.lock();
      return;
    }

    this.setBusy(true);
    this.resetVoiceDemo();
    this.resetReply();
    this.showStatus(true);
    this.history.push({ role: 'user', content: message });
    this.markUsed();
    this.setOrb('thinking');

    const naraBubble = this.addBubble('nara', '');
    let reply = this.fallback;
    let failed = false;

    try {
      await naraSleep(THINKING_MS);
      this.setOrb('processing');
      reply = await this.fetchReply(message);
    } catch {
      failed = true;
      reply = this.fallback;
    }

    this.showStatus(false);
    if (failed) naraBubble.classList.add('nara-landing-ask__bubble--error');
    this.setOrb('speaking');
    const speechPromise = failed
      ? Promise.resolve(null)
      : this.fetchSpeech(reply)
          .then((blob) => this.speak(blob))
          .catch(() => null);

    const durationGuess = Math.max(1400, reply.length * 28);
    await Promise.all([
      this.typewrite(naraBubble, reply, durationGuess),
      speechPromise,
    ]);

    this.history.push({ role: 'assistant', content: reply });
    this.setBusy(false);

    if (this.used() >= this.cap) {
      this.lock();
      this.setOrb('loading');
      return;
    }

    this.setOrb('listening');
    this.syncListenMode();
  }
}

class NaraQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-nara-quiz-step]')];
    this.result = this.querySelector('[data-nara-quiz-result]');
    this.resultText = this.querySelector('[data-nara-quiz-result-text]');
    this.resultLink = this.querySelector('[data-nara-quiz-result-link]');
    this.answers = {};
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.querySelectorAll('[data-nara-quiz-choice]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const question = button.getAttribute('data-nara-quiz-question');
          const value = button.getAttribute('data-nara-quiz-choice');
          if (question) this.answers[question] = value;
          button.parentElement?.querySelectorAll('[data-nara-quiz-choice]').forEach((item) => {
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
          });
          this.advance(button.closest('[data-nara-quiz-step]'));
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /**
   * @param {Element | null} current
   */
  advance(current) {
    const index = this.steps.indexOf(current);
    const next = this.steps[index + 1];
    if (next) {
      current?.setAttribute('hidden', '');
      next.removeAttribute('hidden');
      return;
    }
    current?.setAttribute('hidden', '');
    this.showResult();
  }

  showResult() {
    const need = this.answers.need;
    const map = {
      train: {
        text: 'Start with Training. Nara can shape a session around the time, equipment, and recovery context you share.',
        href: this.getAttribute('data-link-training') || '/pages/training',
        label: 'Explore Training',
      },
      eat: {
        text: 'Start with Nutrition. Eating guidance stays in the same coaching conversation as how you train and recover.',
        href: this.getAttribute('data-link-nutrition') || '/pages/nutrition',
        label: 'Explore Nutrition',
      },
      recover: {
        text: 'Start with Recovery. Readiness can change the day — Nara does not force yesterday’s plan.',
        href: this.getAttribute('data-link-recovery') || '/pages/recovery',
        label: 'Explore Recovery',
      },
      prepare: {
        text: 'Start with Mind. Breathing, focus, and visualization sit next to training — not in a separate silo.',
        href: this.getAttribute('data-link-mind') || '/pages/mind',
        label: 'Explore Mind',
      },
    };
    const selected = map[need] || map.train;
    if (this.resultText) this.resultText.textContent = selected.text;
    if (this.resultLink) {
      this.resultLink.setAttribute('href', selected.href);
      this.resultLink.textContent = selected.label;
    }
    this.result?.removeAttribute('hidden');
  }
}

class NaraSportsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-nara-sports-track]');
    this.viewport = this.querySelector('[data-nara-sports-viewport]');
    this.slides = [...this.querySelectorAll('[data-nara-sports-slide]')];
    this.prevBtn = this.querySelector('[data-nara-sports-prev]');
    this.nextBtn = this.querySelector('[data-nara-sports-next]');
    this.indexEl = this.querySelector('[data-nara-sports-index]');
    this.countEl = this.querySelector('[data-nara-sports-count]');
    this.liveEl = this.querySelector('[data-nara-sports-live]');
    this.motion = this.querySelector('[data-nara-sports-motion]');
    this.benefitsTitle = this.querySelector('[data-nara-sports-benefits-title]');
    this.benefitPoints = [...this.querySelectorAll('[data-nara-sports-point]')];
    this.index = 0;
    this.ready = false;
    this.benefitsAnimating = false;
    this.pendingBenefitsSlide = null;
    this.abort = new AbortController();
    const { signal } = this.abort;

    if (!this.track || this.slides.length === 0) return;

    if (this.countEl) this.countEl.textContent = String(this.slides.length).padStart(2, '0');
    this.goTo(0);
    this.ready = true;

    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
      },
      { signal }
    );
    this.bindSwipe(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  bindSwipe(signal) {
    const surface = this.viewport || this;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    surface.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
      },
      { signal }
    );

    surface.addEventListener(
      'pointerup',
      (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
        if (dx < 0) this.goTo(this.index + 1);
        else this.goTo(this.index - 1);
      },
      { signal }
    );

    surface.addEventListener(
      'pointercancel',
      () => {
        tracking = false;
      },
      { signal }
    );
  }

  goTo(next) {
    const total = this.slides.length;
    const clamped = Math.max(0, Math.min(total - 1, next));
    const changed = clamped !== this.index;
    this.index = clamped;
    this.slides.forEach((slide, i) => {
      if (i === this.index) slide.setAttribute('data-active', '');
      else slide.removeAttribute('data-active');
    });
    if (this.indexEl) this.indexEl.textContent = String(this.index + 1).padStart(2, '0');
    if (this.prevBtn) {
      this.prevBtn.disabled = this.index === 0;
      this.prevBtn.setAttribute('aria-disabled', this.index === 0 ? 'true' : 'false');
    }
    if (this.nextBtn) {
      const atEnd = this.index >= total - 1;
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
    const slide = this.slides[this.index];
    const label = slide?.getAttribute('data-sport-label') || '';
    if (this.liveEl) this.liveEl.textContent = label ? `Showing ${label}` : '';
    this.playBenefits(slide, !this.ready || !changed);
  }

  applyBenefits(slide) {
    if (!slide) return;
    if (this.benefitsTitle) {
      this.benefitsTitle.textContent = slide.getAttribute('data-overlay-title') || 'Nara coaches in real time';
    }
    this.benefitPoints.forEach((el, i) => {
      const value = slide.getAttribute(`data-overlay-${i + 1}`) || '';
      el.textContent = value;
      el.toggleAttribute('hidden', !value);
    });
  }

  playBenefits(slide, immediate = false) {
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduce || !this.motion) {
      this.applyBenefits(slide);
      this.motion?.removeAttribute('data-leave');
      this.motion?.removeAttribute('data-enter');
      return;
    }
    if (this.benefitsAnimating) {
      this.pendingBenefitsSlide = slide;
      return;
    }
    this.runBenefitsMotion(slide);
  }

  async runBenefitsMotion(slide) {
    if (!slide || !this.motion) return;
    this.benefitsAnimating = true;
    this.motion.style.animation = 'none';
    this.motion.removeAttribute('data-enter');
    this.motion.setAttribute('data-leave', '');
    await naraSleep(320);

    const next = this.pendingBenefitsSlide || slide;
    this.pendingBenefitsSlide = null;
    this.applyBenefits(next);
    this.motion.removeAttribute('data-leave');
    this.motion.setAttribute('data-enter', '');
    this.motion.getBoundingClientRect();
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    this.motion.removeAttribute('data-enter');
    await naraSleep(480);

    this.benefitsAnimating = false;
    if (this.pendingBenefitsSlide && this.pendingBenefitsSlide !== next) {
      const queued = this.pendingBenefitsSlide;
      this.pendingBenefitsSlide = null;
      this.runBenefitsMotion(queued);
    }
  }
}

class NaraLivingPlan extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-nara-plan-scene]')];
    this.panel = this.querySelector('[data-nara-plan-panel]');
    this.titleEl = this.querySelector('[data-nara-plan-title]');
    this.textEl = this.querySelector('[data-nara-plan-text]');
    this.device = this.querySelector('[data-nara-plan-device]');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.scenes = this.buildScenes();
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.show(tab.dataset.naraPlanScene), { signal });
    });
    this.show(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.naraPlanScene || 'goals', true);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  buildScenes() {
    return {
      goals: {
        title: this.dataset.goalsTitle,
        text: this.dataset.goalsText,
        status: 'Adapting',
        primary: 'Run 10K',
        secondary: 'Build Strength',
        phase: 'Endurance Foundation',
        week: 'Week 4 of 12',
        milestone: 'Run 6K',
        planStatus: 'Adapting',
        noticed: 'You added strength alongside your 10K goal.',
        adjusting: 'Your week now balances run quality with complementary strength.',
        why: 'Both ambitions stay in one plan, instead of competing for the same days.',
        sessions: [
          { day: 'Mon', name: 'Easy Run · 4K', tag: 'Run', change: 'keep' },
          { day: 'Tue', name: 'Full-body Strength', tag: 'Strength', change: 'added' },
          { day: 'Thu', name: 'Tempo Run', tag: 'Run', change: 'keep' },
          { day: 'Sat', name: 'Long Run · 7K', tag: 'Run', change: 'keep' },
        ],
        months: [
          { label: 'Month 1', name: 'Build Your Foundation', state: 'done' },
          { label: 'Month 2', name: 'Develop Your Performance', state: 'now' },
          { label: 'Month 3', name: 'Reach Your Milestone', state: 'next' },
        ],
        horizon: [
          ['Month 1 — Build Your Foundation', 'Establish consistency, easy aerobic work, and the strength that supports it.'],
          ['Month 2 — Develop Your Performance', 'Raise quality sessions as recovery and available time allow.'],
          ['Month 3 — Reach Your Milestone', 'Sharpen toward the 10K while keeping complementary strength in the mix.'],
        ],
      },
      life: {
        title: this.dataset.lifeTitle,
        text: this.dataset.lifeText,
        status: 'Reshaping',
        primary: 'Run 10K',
        secondary: 'Build Strength',
        phase: 'Endurance Foundation',
        week: 'Week 4 of 12',
        milestone: 'Run 6K',
        planStatus: 'Reshaping week',
        noticed: 'Tuesday is no longer available this week.',
        adjusting: 'Your strength session moves to Wednesday so Tuesday can stay open.',
        why: 'The week still protects recovery while both goals stay on track.',
        sessions: [
          { day: 'Mon', name: 'Easy Run · 4K', tag: 'Run', change: 'keep' },
          { day: 'Wed', name: 'Full-body Strength', tag: 'Strength', change: 'moved' },
          { day: 'Thu', name: 'Tempo Run', tag: 'Run', change: 'keep' },
          { day: 'Sat', name: 'Long Run · 7K', tag: 'Run', change: 'keep' },
        ],
        months: [
          { label: 'Month 1', name: 'Build Your Foundation', state: 'done' },
          { label: 'Month 2', name: 'Develop Your Performance', state: 'now' },
          { label: 'Month 3', name: 'Reach Your Milestone', state: 'next' },
        ],
        horizon: [
          ['Month 1 — Build Your Foundation', 'The destination stays. The week rearranges around real availability.'],
          ['Month 2 — Develop Your Performance', 'Quality work returns when the calendar opens again.'],
          ['Month 3 — Reach Your Milestone', 'Missed days reshape timing — they do not erase the plan.'],
        ],
      },
      progress: {
        title: this.dataset.progressTitle,
        text: this.dataset.progressText,
        status: 'Progressing',
        primary: 'Run 10K',
        secondary: 'Build Strength',
        phase: 'Performance Build',
        week: 'Week 7 of 12',
        milestone: 'Run 8K',
        planStatus: 'Progressing',
        noticed: 'Your endurance has improved over your recent sessions.',
        adjusting: 'Your next training phase has been updated to reflect your progress.',
        why: 'Your plan continues to build toward your 10K goal.',
        sessions: [
          { day: 'Mon', name: 'Steady Run · 6K', tag: 'Run', change: 'keep' },
          { day: 'Wed', name: 'Strength · Lower Power', tag: 'Strength', change: 'keep' },
          { day: 'Fri', name: 'Intervals', tag: 'Run', change: 'added' },
          { day: 'Sun', name: 'Long Run · 9K', tag: 'Run', change: 'keep' },
        ],
        months: [
          { label: 'Month 1', name: 'Foundation complete', state: 'done' },
          { label: 'Month 2', name: 'Performance now', state: 'now' },
          { label: 'Month 3', name: '10K milestone', state: 'next' },
        ],
        horizon: [
          ['Month 1 — Build Your Foundation', 'Completed. Easy volume and supporting strength are in place.'],
          ['Month 2 — Develop Your Performance', 'Current. Quality running and strength now rise together.'],
          ['Month 3 — Reach Your Milestone', 'Next. Sharpen toward your 10K as recovery allows.'],
        ],
      },
    };
  }

  show(sceneKey, immediate = false) {
    const scene = this.scenes[sceneKey];
    if (!scene) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.naraPlanScene === sceneKey;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    const activeTab = this.tabs.find((tab) => tab.dataset.naraPlanScene === sceneKey);
    if (this.panel && activeTab) this.panel.setAttribute('aria-labelledby', activeTab.id);
    if (this.titleEl) this.titleEl.textContent = scene.title;
    if (this.textEl) this.textEl.textContent = scene.text;

    const apply = () => {
      this.applyScene(scene);
      this.device?.setAttribute('data-scene', sceneKey);
      this.device?.removeAttribute('data-updating');
    };

    if (immediate || reduce || !this.device) {
      apply();
      return;
    }

    this.device.setAttribute('data-updating', '');
    window.setTimeout(apply, 220);
  }

  applyScene(scene) {
    const set = (sel, value) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = value;
    };
    set('[data-nara-plan-status]', scene.status);
    set('[data-nara-plan-primary]', scene.primary);
    set('[data-nara-plan-secondary]', scene.secondary);
    set('[data-nara-plan-phase]', scene.phase);
    set('[data-nara-plan-week]', scene.week);
    set('[data-nara-plan-milestone]', scene.milestone);
    set('[data-nara-plan-planstatus]', scene.planStatus);
    set('[data-nara-plan-noticed]', scene.noticed);
    set('[data-nara-plan-adjusting]', scene.adjusting);
    set('[data-nara-plan-why]', scene.why);

    const sessions = this.querySelector('[data-nara-plan-sessions]');
    if (sessions) {
      sessions.replaceChildren(
        ...scene.sessions.map((item) => {
          const li = document.createElement('li');
          li.dataset.change = item.change;
          li.innerHTML = `<span>${item.day}</span><strong>${item.name}</strong><em>${item.tag}</em>`;
          return li;
        })
      );
    }

    const months = this.querySelector('[data-nara-plan-months]');
    if (months) {
      months.replaceChildren(
        ...scene.months.map((item) => {
          const li = document.createElement('li');
          li.dataset.state = item.state;
          li.innerHTML = `<span>${item.label}</span><strong>${item.name}</strong>`;
          return li;
        })
      );
    }

    const horizon = this.querySelector('[data-nara-plan-horizon]');
    if (horizon) {
      horizon.replaceChildren(
        ...scene.horizon.map((item, index) => {
          const li = document.createElement('li');
          li.innerHTML = `<span>0${index + 1}</span><div><strong>${item[0]}</strong><p>${item[1]}</p></div>`;
          return li;
        })
      );
    }
  }
}

if (!customElements.get('nara-living-plan')) {
  customElements.define('nara-living-plan', NaraLivingPlan);
}

class NaraChoiceSwitch extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-nara-choice]')];
    this.panels = [...this.querySelectorAll('[data-nara-choice-panel]')];
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.show(button.dataset.naraChoice), { signal });
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  show(key) {
    this.buttons.forEach((button) => {
      button.setAttribute('aria-selected', button.dataset.naraChoice === key ? 'true' : 'false');
    });
    this.panels.forEach((panel) => {
      const match = panel.dataset.naraChoicePanel === key;
      panel.hidden = !match;
    });
    const active = this.buttons.find((button) => button.dataset.naraChoice === key);
    this.setAttribute('data-phase', key);
    const day = this.querySelector('[data-nara-cycle-day]');
    const phase = this.querySelector('[data-nara-cycle-phase]');
    if (day && active?.dataset.day) day.textContent = active.dataset.day;
    if (phase && active?.dataset.phase) phase.textContent = active.dataset.phase;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.panels.forEach((panel) => {
      if (panel.hidden) return;
      const track = panel.querySelector('[data-nara-carousel-track]');
      track?.scrollTo({ left: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }
}

class NaraCardCarousel extends HTMLElement {
  static get observedAttributes() {
    return ['data-inview', 'data-pending'];
  }

  connectedCallback() {
    this.track = this.querySelector('[data-nara-carousel-track]');
    this.prev = this.querySelector('[data-nara-carousel-prev]');
    this.next = this.querySelector('[data-nara-carousel-next]');
    this.dotsHost = this.querySelector('[data-nara-carousel-dots]');
    this.indexEl = this.querySelector('[data-nara-carousel-index]');
    this.countEl = this.querySelector('[data-nara-carousel-count]');
    this.abort = new AbortController();
    this.pinToStart();
    const { signal } = this.abort;
    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      },
      { signal }
    );
    this.buildDots();
    this.track?.addEventListener('scroll', () => this.syncUi(), { signal, passive: true });
    window.addEventListener('resize', () => this.syncUi(), { signal, passive: true });
    this.syncUi();
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this._naraPinFrame) window.cancelAnimationFrame(this._naraPinFrame);
  }

  attributeChangedCallback(name) {
    if (name === 'data-pending' && this.hasAttribute('data-pending')) {
      this.removeAttribute('data-dealt');
      this.pinToStart();
    }
    if (name === 'data-inview' && this.hasAttribute('data-inview')) {
      this.pinToStart();
    }
  }

  pinToStart() {
    if (!this.track || !this.classList.contains('nara-landing-modules__carousel')) return;
    const lock = () => {
      this.track.scrollLeft = 0;
    };
    lock();
    if (this._naraPinFrame) {
      window.cancelAnimationFrame(this._naraPinFrame);
      this._naraPinFrame = 0;
    }
    if (!this.hasAttribute('data-inview')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('data-dealt', '');
      this.syncUi();
      return;
    }
    const started = performance.now();
    const hold = (now) => {
      lock();
      if (now - started < 1500) {
        this._naraPinFrame = window.requestAnimationFrame(hold);
        return;
      }
      this._naraPinFrame = 0;
      this.setAttribute('data-dealt', '');
      lock();
      this.syncUi();
    };
    this._naraPinFrame = window.requestAnimationFrame(hold);
  }

  cards() {
    if (!this.track) return [];
    return [...this.track.querySelectorAll('.nara-train-device, .nara-landing-modules__card')];
  }

  cardWidth() {
    const card = this.cards()[0];
    if (!(card instanceof HTMLElement) || !this.track) return 280;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return card.offsetWidth + gap;
  }

  currentIndex() {
    const total = this.cards().length;
    if (!this.track || !total) return 0;
    const width = this.cardWidth() || 1;
    return Math.max(0, Math.min(total - 1, Math.round(this.track.scrollLeft / width)));
  }

  step(direction) {
    if (!this.track) return;
    const next = this.currentIndex() + direction;
    this.goTo(next);
  }

  goTo(index) {
    if (!this.track) return;
    const total = this.cards().length;
    const target = Math.max(0, Math.min(total - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target * this.cardWidth(), behavior: reduce ? 'auto' : 'smooth' });
  }

  buildDots() {
    if (!this.dotsHost) return;
    this.dotsHost.replaceChildren();
    this.dots = this.cards().map((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nara-landing-modules__dot';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-label', `Show card ${index + 1}`);
      button.addEventListener('click', () => this.goTo(index), { signal: this.abort.signal });
      this.dotsHost.append(button);
      return button;
    });
  }

  syncUi() {
    const cards = this.cards();
    if (!this.track || !cards.length) return;
    const current = this.currentIndex();
    const total = cards.length;

    if (this.dots?.length) {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });
    }

    if (this.indexEl) this.indexEl.textContent = String(current + 1).padStart(2, '0');
    if (this.countEl) this.countEl.textContent = String(total).padStart(2, '0');

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 4;
    const atEnd = this.track.scrollLeft >= maxScroll - 4 || maxScroll <= 4;
    if (this.prev) {
      this.prev.disabled = atStart;
      this.prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
    if (this.next) {
      this.next.disabled = atEnd;
      this.next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  syncDots() {
    this.syncUi();
  }
}

if (!customElements.get('nara-choice-switch')) {
  customElements.define('nara-choice-switch', NaraChoiceSwitch);
}

if (!customElements.get('nara-card-carousel')) {
  customElements.define('nara-card-carousel', NaraCardCarousel);
}

if (!customElements.get('nara-landing-nav')) {
  customElements.define('nara-landing-nav', NaraLandingNav);
}

if (!customElements.get('nara-ask-preview')) {
  customElements.define('nara-ask-preview', NaraAskPreview);
}

if (!customElements.get('nara-sports-carousel')) {
  customElements.define('nara-sports-carousel', NaraSportsCarousel);
}

if (!customElements.get('nara-quiz')) {
  customElements.define('nara-quiz', NaraQuiz);
}

class NaraSystemAdapt extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panel = this.querySelector('[data-nara-system-panel]');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.scenarios = this.readScenarios();
    this.activeKey = null;
    this.pendingKey = null;
    this.animating = false;
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.naraSystemTab), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          this.onKey(event);
        },
        { signal }
      );
    });
    this.bindSwipe(signal);
    const current = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(current?.dataset.naraSystemTab || 'less-time', true);
    this.preloadPhotos();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  motionNodes() {
    return [...this.querySelectorAll('[data-nara-system-motion]')];
  }

  readScenarios() {
    const node = this.querySelector('[data-nara-system-data]');
    try {
      return JSON.parse(node?.textContent || '{}');
    } catch {
      return {};
    }
  }

  bindSwipe(signal) {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('[role="tab"]')) {
          tracking = false;
          return;
        }
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
      },
      { signal }
    );

    this.addEventListener(
      'pointerup',
      (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
        const keys = this.tabs.map((tab) => tab.dataset.naraSystemTab).filter(Boolean);
        if (!keys.length) return;
        const current = Math.max(0, keys.indexOf(this.activeKey));
        const next = dx < 0
          ? (current + 1) % keys.length
          : (current - 1 + keys.length) % keys.length;
        this.select(keys[next]);
      },
      { signal }
    );

    this.addEventListener(
      'pointercancel',
      () => {
        tracking = false;
      },
      { signal }
    );
  }

  onKey(event) {
    const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (offset == null && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = current;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else next = (current + offset + this.tabs.length) % this.tabs.length;
    this.tabs[next].focus();
    this.select(this.tabs[next].dataset.naraSystemTab);
  }

  syncTabs(key) {
    this.tabs.forEach((tab) => {
      const on = tab.dataset.naraSystemTab === key;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    const active = this.tabs.find((tab) => tab.dataset.naraSystemTab === key);
    if (this.panel && active) this.panel.setAttribute('aria-labelledby', active.id);
  }

  select(key, immediate = false) {
    const scene = this.scenarios[key];
    if (!scene) return;

    this.syncTabs(key);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduce || !this.panel) {
      this.activeKey = key;
      this.pendingKey = null;
      this.render(scene);
      this.visual()?.removeAttribute('data-leave');
      this.motionNodes().forEach((el) => {
        el.removeAttribute('data-leave');
        el.removeAttribute('data-enter');
      });
      return;
    }

    if (key === this.activeKey && !this.animating) return;
    if (this.animating) {
      this.pendingKey = key;
      return;
    }
    this.runMotion(key);
  }

  async runMotion(key) {
    const scene = this.scenarios[key];
    if (!scene) return;

    this.animating = true;
    const motions = this.motionNodes();
    const visual = this.visual();
    motions.forEach((el) => {
      el.removeAttribute('data-enter');
      el.setAttribute('data-leave', '');
    });
    visual?.setAttribute('data-leave', '');
    await naraSleep(360);

    const nextKey = this.pendingKey || key;
    this.pendingKey = null;
    const nextScene = this.scenarios[nextKey] || scene;
    this.syncTabs(nextKey);
    this.render(nextScene);
    this.activeKey = nextKey;
    visual?.removeAttribute('data-leave');

    motions.forEach((el) => {
      el.removeAttribute('data-leave');
      el.setAttribute('data-enter', '');
    });
    motions[0]?.getBoundingClientRect();
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    motions.forEach((el) => el.removeAttribute('data-enter'));
    await naraSleep(480);

    this.animating = false;
    if (this.pendingKey && this.pendingKey !== this.activeKey) {
      const queued = this.pendingKey;
      this.pendingKey = null;
      this.runMotion(queued);
    }
  }

  render(scene) {
    const set = (sel, value) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = value;
    };
    set('[data-nara-system-insight]', scene.insight);
    set('[data-nara-system-result-title]', scene.resultTitle);
    set('[data-nara-system-before]', this.wrapTimeLine(scene.before));
    set('[data-nara-system-after]', this.wrapTimeLine(scene.after));
    set('[data-nara-system-close]', String(scene.close || '').replace(/^(.+?\.)\s+/, '$1\n'));
    this.setIcon('[data-nara-system-before-icon]', scene.beforeIcon);
    this.setIcon('[data-nara-system-after-icon]', scene.afterIcon);
    this.setPhoto(scene);
  }

  wrapTimeLine(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    const parts = text.split(' ');
    if (parts.length < 2) return text;
    return `${parts.slice(0, -1).join(' ')}\n${parts[parts.length - 1]}`;
  }

  visual() {
    return this.querySelector('.nara-landing-system__visual');
  }

  preloadPhotos() {
    Object.values(this.scenarios).forEach((scene) => {
      if (scene?.image) {
        const img = new Image();
        img.src = scene.image;
      }
      if (scene?.imageMobile) {
        const img = new Image();
        img.src = scene.imageMobile;
      }
    });
  }

  setPhoto(scene) {
    const img = this.querySelector('[data-nara-system-photo]');
    const source = this.querySelector('[data-nara-system-source]');
    if (!img || !scene?.image) return;
    const desktop = window.matchMedia('(min-width: 768px)').matches;
    if (source) source.srcset = scene.image;
    img.src = desktop ? scene.image : scene.imageMobile || scene.image;
    if (scene.imageAlt) img.alt = scene.imageAlt;
  }

  setIcon(sel, name) {
    const host = this.querySelector(sel);
    const template = this.querySelector(`[data-nara-icon="${name}"]`);
    if (!host || !(template instanceof HTMLTemplateElement)) return;
    host.replaceChildren(template.content.cloneNode(true));
  }
}

if (!customElements.get('nara-system-adapt')) {
  customElements.define('nara-system-adapt', NaraSystemAdapt);
}

class NaraWearableSignals extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[data-nara-signal-panel]')];
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.naraSignal), { signal });
      tab.addEventListener('keydown', (event) => this.onKey(event), { signal });
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  onKey(event) {
    const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1, Home: 'start', End: 'end' }[event.key];
    if (offset == null) return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = current;
    if (offset === 'start') next = 0;
    else if (offset === 'end') next = this.tabs.length - 1;
    else next = (current + offset + this.tabs.length) % this.tabs.length;
    this.tabs[next].focus();
    this.select(this.tabs[next].dataset.naraSignal);
  }

  select(key) {
    if (!key) return;
    this.tabs.forEach((tab) => {
      const on = tab.dataset.naraSignal === key;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      const on = panel.dataset.naraSignalPanel === key;
      if (on) {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      }
    });
  }
}

if (!customElements.get('nara-wearable-signals')) {
  customElements.define('nara-wearable-signals', NaraWearableSignals);
}

class NaraCycleIntelligence extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.data = this.readData();
    this.phases = Array.isArray(this.data.phases) ? this.data.phases : [];
    this.cycleLength = Number(this.data.cycleLength || 28);
    const found = this.phases.findIndex((phase) => phase.key === (this.dataset.phase || 'luteal'));
    this.phaseIndex = found >= 0 ? found : Math.max(0, this.phases.length - 1);

    this.sheet = this.querySelector('[data-nara-cycle-sheet]');

    this.querySelector('[data-nara-cycle-open-adapt]')?.addEventListener(
      'click',
      () => this.openSheet(),
      { signal }
    );
    this.querySelector('[data-nara-cycle-close-adapt]')?.addEventListener(
      'click',
      () => this.closeSheet(),
      { signal }
    );
    this.sheet?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.sheet) this.closeSheet();
      },
      { signal }
    );

    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  readData() {
    const node = this.querySelector('[data-nara-cycle-data]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return {};
    }
  }

  currentPhase() {
    return this.phases[this.phaseIndex] || {
      key: 'luteal',
      label: 'Luteal Phase',
      day: 18,
      energy: 'Moderate',
      energyLevel: 2,
      mood: 'Sensitive',
      symptoms: 'Cramps, Fatigue',
      adaptation: "Adjust today's training intensity and prioritize recovery based on how you feel.",
      training: 'Suggested: lower-impact or moderated intensity.',
      nutrition: 'Suggested: supportive meals and hydration.',
      recovery: 'Suggested: additional recovery time.',
    };
  }

  openSheet() {
    if (!(this.sheet instanceof HTMLDialogElement)) return;
    if (typeof this.sheet.showModal === 'function') this.sheet.showModal();
    else this.sheet.setAttribute('open', '');
  }

  closeSheet() {
    if (!(this.sheet instanceof HTMLDialogElement)) return;
    if (typeof this.sheet.close === 'function') this.sheet.close();
    else this.sheet.removeAttribute('open');
  }

  setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value;
  }

  moodLevel(mood) {
    const map = { Low: 1, Sensitive: 2, Calm: 3, Happy: 4, Rising: 3, High: 5 };
    return map[mood] || 2;
  }

  render() {
    const phase = this.currentPhase();
    this.dataset.phase = phase.key;
    this.dataset.day = String(phase.day);

    this.setText('[data-nara-cycle-energy]', phase.energy);
    this.setText('[data-nara-cycle-mood]', phase.mood);
    this.setText('[data-nara-cycle-symptoms]', phase.symptoms);
    this.setText('[data-nara-cycle-adaptation]', phase.adaptation);
    this.setText('[data-nara-cycle-adaptation-sheet]', phase.adaptation);
    this.setText('[data-nara-cycle-training]', phase.training);
    this.setText('[data-nara-cycle-nutrition]', phase.nutrition);
    this.setText('[data-nara-cycle-recovery]', phase.recovery);

    const bars = this.querySelector('[data-nara-cycle-energy-bars]');
    if (bars) {
      const level = Number(phase.energyLevel) || 1;
      [...bars.querySelectorAll('i')].forEach((bar, index) => {
        bar.classList.toggle('is-on', index < level);
      });
    }

    const dots = this.querySelector('[data-nara-cycle-mood-dots]');
    if (dots) {
      const level = this.moodLevel(phase.mood);
      [...dots.querySelectorAll('i')].forEach((dot, index) => {
        dot.classList.toggle('is-on', index < level);
      });
    }
  }
}

if (!customElements.get('nara-cycle-intelligence')) {
  customElements.define('nara-cycle-intelligence', NaraCycleIntelligence);
}

class NaraHowJourney extends HTMLElement {
  connectedCallback() {
    this.rings = [...this.querySelectorAll('[data-nara-how-ring]')];
    this.adapt = this.querySelector('[data-nara-how-adapt]');
    this.abort = new AbortController();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.rings.forEach((ring) => {
      const value = Number(ring.dataset.naraHowRing || 0);
      const length = 163.4;
      ring.style.strokeDasharray = `${length}`;
      ring.style.strokeDashoffset = reduce ? `${length * (1 - value)}` : `${length}`;
    });

    if (reduce) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.rings.forEach((ring) => {
            const value = Number(ring.dataset.naraHowRing || 0);
            ring.style.strokeDashoffset = `${163.4 * (1 - value)}`;
          });
          io.disconnect();
        });
      },
      { threshold: 0.35 }
    );
    const progress = this.querySelector('.nara-landing-how__glass--progress');
    if (progress) io.observe(progress);
    this.abort.signal.addEventListener('abort', () => io.disconnect());
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}

if (!customElements.get('nara-how-journey')) {
  customElements.define('nara-how-journey', NaraHowJourney);
}

class NaraMembershipPlans extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.abort = new AbortController();
    const { signal } = this.abort;
    try {
      this.data = JSON.parse(this.querySelector('[data-nara-membership-data]')?.textContent || '{}');
    } catch {
      this.data = {};
    }
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.naraPeriod), { signal });
      tab.addEventListener('keydown', (event) => this.onKey(event), { signal });
    });
    const requested = new URLSearchParams(window.location.search).get('period');
    const current = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const initial =
      requested === 'monthly' || requested === 'yearly'
        ? requested
        : current?.dataset.naraPeriod || this.dataset.period || 'yearly';
    this.select(initial);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  onKey(event) {
    const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1, Home: 'start', End: 'end' }[event.key];
    if (offset == null) return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = current;
    if (offset === 'start') next = 0;
    else if (offset === 'end') next = this.tabs.length - 1;
    else next = (current + offset + this.tabs.length) % this.tabs.length;
    this.tabs[next].focus();
    this.select(this.tabs[next].dataset.naraPeriod);
  }

  select(period) {
    if (!period || !this.data.plans) return;
    this.dataset.period = period;
    this.tabs.forEach((tab) => {
      const on = tab.dataset.naraPeriod === period;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.querySelectorAll('[data-nara-plan]').forEach((card) => {
      const offer = this.data.plans[card.dataset.naraPlan]?.[period];
      if (!offer) return;
      const set = (sel, value) => {
        const el = card.querySelector(sel);
        if (el) el.textContent = value;
      };
      set('[data-nara-trial]', offer.trial || '');
      set('[data-nara-price-lead]', offer.priceLead || '');
      set('[data-nara-price]', offer.price);
      set('[data-nara-price-period]', offer.period);
      set('[data-nara-equivalent]', offer.equivalent || '');
      set('[data-nara-save]', offer.save || '');
      set(
        '[data-nara-reassure]',
        period === 'monthly' ? 'Cancel anytime · No long-term commitment' : ''
      );
      set('[data-nara-billing]', offer.billing);
      const cta = card.querySelector('[data-nara-plan-cta]');
      if (cta) {
        cta.textContent = offer.cta;
        if (offer.href) cta.setAttribute('href', offer.href);
      }
    });
  }
}

if (!customElements.get('nara-membership-plans')) {
  customElements.define('nara-membership-plans', NaraMembershipPlans);
}

class NaraFeatureExpand extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-nara-expand]');
    this.panel = this.querySelector('[data-nara-expand-panel]');
    this.label = this.querySelector('[data-nara-expand-label]');
    if (!this.button || !this.panel) return;
    this.openLabel = this.dataset.openLabel || 'Explore all features';
    this.closeLabel = this.dataset.closeLabel || 'Show less';
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.button.addEventListener('click', () => this.toggle(), { signal });
    this.panel.addEventListener('transitionend', (event) => this.onTransitionEnd(event), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  toggle() {
    if (this.hasAttribute('data-open')) this.close();
    else this.open();
  }

  open() {
    this.panel.hidden = false;
    this.panel.removeAttribute('inert');
    this.button.setAttribute('aria-expanded', 'true');
    if (this.label) this.label.textContent = this.closeLabel;
    requestAnimationFrame(() => {
      this.setAttribute('data-open', '');
    });
  }

  close() {
    this.removeAttribute('data-open');
    this.button.setAttribute('aria-expanded', 'false');
    if (this.label) this.label.textContent = this.openLabel;
    this.panel.setAttribute('inert', '');
    if (this.reduce.matches) {
      this.hidePanel();
      return;
    }
    window.setTimeout(() => {
      if (!this.hasAttribute('data-open')) this.hidePanel();
    }, 340);
  }

  onTransitionEnd(event) {
    if (event.target !== this.panel) return;
    if (this.hasAttribute('data-open')) return;
    this.hidePanel();
  }

  hidePanel() {
    this.panel.hidden = true;
    this.panel.setAttribute('inert', '');
  }
}

if (!customElements.get('nara-feature-expand')) {
  customElements.define('nara-feature-expand', NaraFeatureExpand);
}

class NaraProductUpdates extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-nara-updates-submit]');
    if (!this.form || !this.button) return;
    this.abort = new AbortController();
    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  onSubmit(event) {
    if (!this.form.checkValidity()) return;
    event.preventDefault();
    if (this.form.dataset.submitting === 'true') return;
    this.form.dataset.submitting = 'true';
    this.markDone();
    const body = new FormData(this.form);
    fetch(this.form.action || window.location.pathname, {
      method: 'POST',
      body,
      headers: { Accept: 'text/html' },
    }).catch(() => {});
  }

  markDone() {
    const label = this.button.getAttribute('data-success-label') || "You're in!";
    this.button.disabled = true;
    this.button.textContent = label;
    const email = this.form.querySelector('input[type="email"]');
    if (email instanceof HTMLInputElement) email.setAttribute('readonly', '');
  }
}

if (!customElements.get('nara-product-updates')) {
  customElements.define('nara-product-updates', NaraProductUpdates);
}

class NaraFaqAccordion extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.nara-landing-faq__item')];
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.items.forEach((item) => {
      const trigger = item.querySelector('.nara-landing-faq__trigger');
      const panel = item.querySelector('.nara-landing-faq__panel');
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => this.toggle(item), { signal });
      panel.addEventListener('transitionend', (event) => this.onTransitionEnd(item, event), { signal });
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  toggle(item) {
    if (item.hasAttribute('data-open')) {
      this.close(item);
      return;
    }
    this.items.forEach((other) => {
      if (other !== item && other.hasAttribute('data-open')) this.close(other);
    });
    this.open(item);
  }

  open(item) {
    const trigger = item.querySelector('.nara-landing-faq__trigger');
    const panel = item.querySelector('.nara-landing-faq__panel');
    panel.hidden = false;
    panel.removeAttribute('inert');
    trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      item.setAttribute('data-open', 'true');
    });
  }

  close(item) {
    const trigger = item.querySelector('.nara-landing-faq__trigger');
    const panel = item.querySelector('.nara-landing-faq__panel');
    item.removeAttribute('data-open');
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('inert', '');
    if (this.reduce.matches) {
      this.hidePanel(panel);
      return;
    }
    window.setTimeout(() => {
      if (!item.hasAttribute('data-open')) this.hidePanel(panel);
    }, 260);
  }

  onTransitionEnd(item, event) {
    if (event.propertyName !== 'grid-template-rows') return;
    if (item.hasAttribute('data-open')) return;
    this.hidePanel(item.querySelector('.nara-landing-faq__panel'));
  }

  hidePanel(panel) {
    if (!panel) return;
    panel.hidden = true;
    panel.setAttribute('inert', '');
  }
}

if (!customElements.get('nara-faq-accordion')) {
  customElements.define('nara-faq-accordion', NaraFaqAccordion);
}

function naraRomColor(percent) {
  if (percent >= 85) return '#22C55E';
  if (percent >= 70) return '#EAB308';
  if (percent >= 50) return '#F97316';
  return '#EF4444';
}

function naraRomEase(t) {
  return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;
}

function naraSyncRomGauges() {
  const ARC = 270;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-nara-rom]').forEach((root) => {
    const fill = root.querySelector('.nara-landing-ecosystem__rom-fill');
    const value = root.querySelector('.nara-landing-ecosystem__rom-value');
    if (!(fill instanceof SVGPathElement) || !(value instanceof HTMLElement)) return;

    const paint = (percent) => {
      const p = Math.max(0, Math.min(100, percent));
      const color = naraRomColor(p);
      fill.setAttribute('stroke-dashoffset', String(ARC * (1 - p / 100)));
      fill.setAttribute('stroke', color);
      value.textContent = `${Math.round(p)}%`;
      value.style.color = color;
    };

    if (reduce) {
      paint(72);
      return;
    }

    const floor = 10;
    const nextPeak = () => 54 + Math.round(Math.random() * 42);
    let from = floor;
    let peak = nextPeak();
    let goingUp = true;
    let start = null;
    let duration = 2200;

    const beginPhase = (now) => {
      start = now;
      if (goingUp) {
        from = floor + Math.round(Math.random() * 8);
        peak = nextPeak();
        duration = 2000 + Math.round(Math.random() * 900);
      } else {
        duration = 700 + Math.round(Math.random() * 400);
      }
    };

    const tick = (now) => {
      if (start == null) beginPhase(now);
      const t = Math.min(1, (now - start) / duration);
      const eased = naraRomEase(t);
      paint(goingUp ? from + (peak - from) * eased : peak + (from - peak) * eased);
      if (t >= 1) {
        goingUp = !goingUp;
        beginPhase(now);
      }
      root._naraRomFrame = window.requestAnimationFrame(tick);
    };

    if (root._naraRomFrame) window.cancelAnimationFrame(root._naraRomFrame);
    root._naraRomFrame = window.requestAnimationFrame(tick);
  });
}

function naraNavType() {
  const nav = performance.getEntriesByType?.('navigation')?.[0];
  if (nav?.type) return nav.type;
  if (performance.navigation?.type === 1) return 'reload';
  return 'navigate';
}

function naraIsReload() {
  return naraNavType() === 'reload';
}

function naraShouldResetToHeader() {
  const type = naraNavType();
  if (type === 'reload') return true;
  if (type === 'back_forward') return false;
  return !location.hash;
}

function naraScrollToTop() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function naraShowHeader() {
  document.querySelectorAll('nara-landing-nav').forEach((nav) => {
    nav.removeAttribute('data-hidden');
    nav.lastY = 0;
  });
}

function naraRevealHeroNow() {
  document.querySelectorAll('.nara-landing-hero [data-nara-reveal]').forEach((el) => {
    el.removeAttribute('data-pending');
    el.setAttribute('data-inview', '');
  });
}

function naraPrepareReloadReplay() {
  if (!naraShouldResetToHeader()) return;
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
  naraScrollToTop();
  naraShowHeader();
  document.querySelectorAll('[data-nara-reveal]').forEach((el) => {
    if (el.closest('.nara-landing-hero')) return;
    el.removeAttribute('data-inview');
    el.removeAttribute('data-dealt');
    el.setAttribute('data-pending', '');
  });
  naraRevealHeroNow();
}

function naraHoldHeaderAfterReload() {
  if (!naraShouldResetToHeader()) return;
  naraPrepareReloadReplay();
  let frames = 0;
  const hold = () => {
    naraScrollToTop();
    naraShowHeader();
    frames += 1;
    if (frames < 24) requestAnimationFrame(hold);
  };
  hold();
  [50, 120, 240, 400].forEach((ms) => {
    window.setTimeout(() => {
      naraScrollToTop();
      naraShowHeader();
    }, ms);
  });
}

function naraRevealOnView() {
  naraPrepareReloadReplay();
  naraRevealHeroNow();
  const nodes = [...document.querySelectorAll('[data-nara-reveal]')];
  if (!nodes.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) {
    nodes.forEach((el) => el.setAttribute('data-inview', ''));
    return;
  }

  const reveal = (el) => {
    el.removeAttribute('data-pending');
    el.setAttribute('data-inview', '');
  };

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.22, rootMargin: '0px 0px -8% 0px' }
  );

  nodes.forEach((el) => {
    if (el.closest('.nara-landing-hero')) {
      reveal(el);
      return;
    }
    if (!el.hasAttribute('data-inview')) el.setAttribute('data-pending', '');
    io.observe(el);
  });
  window.setTimeout(() => {
    nodes.forEach((el) => {
      if (el.hasAttribute('data-inview')) return;
      const rect = el.getBoundingClientRect();
      const visible = rect.bottom > 40 && rect.top < window.innerHeight * 0.92;
      if (visible) reveal(el);
    });
  }, 120);
}

function naraLockHorizontalPan() {
  if (!window.matchMedia('(max-width: 899px)').matches) return;

  let startX = 0;
  let startY = 0;

  document.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1) return;
      if (
        event.target instanceof Element &&
        event.target.closest('[data-nara-carousel-track], nara-card-carousel, nara-sports-carousel, [data-nara-sports-viewport], nara-system-adapt, [data-nara-cycle-insights], nara-cycle-intelligence')
      ) {
        return;
      }
      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);
      if (dx > dy && dx > 10) event.preventDefault();
    },
    { passive: false }
  );
}

try {
  history.scrollRestoration = 'manual';
} catch (error) {
  /* ignore */
}

if (naraShouldResetToHeader()) {
  naraHoldHeaderAfterReload();
  window.addEventListener('load', naraHoldHeaderAfterReload, { once: true });
  window.addEventListener('pageshow', () => {
    naraShowHeader();
    if (naraShouldResetToHeader()) naraHoldHeaderAfterReload();
  });
}

window.addEventListener('beforeunload', () => {
  try {
    history.scrollRestoration = 'manual';
  } catch (error) {
    /* ignore */
  }
  naraScrollToTop();
});
window.addEventListener('pagehide', naraScrollToTop);

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      naraSyncRomGauges();
      naraRevealOnView();
      naraLockHorizontalPan();
    },
    { once: true }
  );
} else {
  naraSyncRomGauges();
  naraRevealOnView();
  naraLockHorizontalPan();
}

document.addEventListener(
  'click',
  (event) => {
    const cta = event.target instanceof Element ? event.target.closest('[data-nara-cta]') : null;
    if (cta) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'nara_cta',
        cta: cta.getAttribute('data-nara-cta'),
      });
    }
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href*="#"]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    const id = url.hash.slice(1);
    if (!id) return;
    const destination = document.getElementById(id);
    if (!destination) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    destination.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (destination instanceof HTMLElement) {
      destination.setAttribute('tabindex', '-1');
      destination.focus({ preventScroll: true });
    }
  },
  true
);
